import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { Input } from 'app/design/primitives/input';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import type { CreateTokenDTO } from 'app/network/identity';
import { createTheme as createDSTheme } from 'app/shopify';
import type React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View, type TextInput } from 'react-native';
import type { IntroPageSharedProps } from './intro-page.types';
import { IntroSideCardSlider } from './intro-side-card-slider.large';
import { IntroSSOSection } from './intro-sso-section.large';
import { LastUsedAuthenticationMethodBadge } from 'app/components/authentication/AuthenticationMethodOption';
import type { AuthenticationMethod } from 'app/lib/authentication-method';

const theme = createDSTheme();

export function IntroLoginPage({
  lastUsedMethod,
  control,
  createIdentity,
  didSSOLogin,
  goBackWhenIsLoggedIn,
  isFormValid,
  onForgotPassword,
  onLayoutSSO,
  onSubmit,
  onToggleForm,
  refPassword,
  refSSO,
  renderPartners,
  renderSSOButtons,
  ssoError,
}: IntroPageSharedProps & {
  lastUsedMethod: AuthenticationMethod | undefined;
  control: Control<CreateTokenDTO>;
  isFormValid: boolean;
  onForgotPassword: () => void;
  onSubmit: () => void | Promise<void>;
  refPassword: React.RefObject<TextInput | null>;
}) {
  const { t: tLogin } = useTranslation('login');
  const { t: tAPI } = useTranslation('api-errors');
  return (
    <Row backgroundColor="surface.tertiary" flex={1}>
      <Column
        gap="spacing.general.x4"
        flex={1}
        justifyContent="center"
        alignItems="center"
        paddingTop="spacing.general.x12"
      >
        <View
          style={{
            marginRight: theme.spacing['spacing.general.x12'] + 20,
            marginLeft: theme.spacing['spacing.general.x12'] + 20,
            maxWidth: 520,
            width: '80%',
            gap: theme.spacing['spacing.general.x4'],
            backgroundColor: 'white',
            padding: theme.spacing['spacing.general.x4'],
            borderRadius: theme.borderRadii['radius.lg'],
          }}
        >
          <Text
            fontWeight="700"
            marginBottom="spacing.general.x2"
            style={{ marginHorizontal: 16 + 30 }}
            textAlign="center"
            variant="headerXSmall"
          >
            {tLogin('title')}
          </Text>

          <IntroSSOSection {...{ onLayoutSSO, refSSO, renderSSOButtons, ssoError }} />

          <Column gap="spacing.general.x5">
            <Controller
              {...{ control }}
              name="email"
              render={({ field, fieldState }) => (
                <Input
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect={false}
                  error={fieldState.error}
                  label={tLogin('emailFieldLabel')}
                  labelRight={lastUsedMethod === 'Email' ? <LastUsedAuthenticationMethodBadge method="Email" /> : null}
                  onBlur={field.onBlur}
                  onChangeText={field.onChange}
                  onSubmitEditing={() => refPassword.current?.focus()}
                  placeholder={tLogin('emailFieldPlaceholder')}
                  returnKeyType="next"
                  submitBehavior="submit"
                  textContentType="emailAddress"
                  value={field.value}
                />
              )}
            />

            <Controller
              {...{ control }}
              name="password"
              render={({ field, fieldState }) => (
                <Input
                  autoCapitalize="none"
                  autoComplete="password"
                  autoCorrect={false}
                  error={fieldState.error}
                  innerRef={refPassword}
                  label={tLogin('passwordFieldLabel')}
                  onBlur={field.onBlur}
                  onChangeText={field.onChange}
                  onSubmitEditing={() => isFormValid && void onSubmit()}
                  placeholder={tLogin('passwordFieldPlaceholder')}
                  returnKeyType="go"
                  secureTextEntry
                  textContentType="password"
                  value={field.value}
                />
              )}
            />
          </Column>

          {createIdentity.error ? (
            <RemoteError {...{ error: createIdentity.error, message: tAPI(createIdentity.error.message) }} />
          ) : null}

          <Button
            onPress={onForgotPassword}
            pressableType={PressableType.GestureHandler}
            marginTop="spacing.general.x2"
          >
            <Text flex={1} fontWeight="700" textAlign="center" textDecorationLine="underline">
              {tLogin('forgotPassword')}
            </Text>
          </Button>

          <Button
            loading={createIdentity.isPending || (goBackWhenIsLoggedIn && !didSSOLogin)}
            pressableType={PressableType.GestureHandler}
            onPress={() => void onSubmit()}
            variant="signup"
            style={{ width: '100%' }}
          >
            {tLogin('primaryButtonText')}
          </Button>

          <Button onPress={onToggleForm} variant="plain" marginBottom="spacing.general.x5">
            <Row gap="spacing.general.x1">
              <Text color="text.tertiary">{tLogin('noAccountYetPrefix')}</Text>
              <Text fontWeight="700" textDecorationLine="underline" color="surface.brand.default">
                {tLogin('noAccountYet')}
              </Text>
            </Row>
          </Button>

          {renderPartners()}
        </View>
      </Column>
      <IntroSideCardSlider />
    </Row>
  );
}
